import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import DocumentUpload from "@/components/DocumentUpload";
import { Info, Plus, Trash2, Ban } from "lucide-react";
import { blockedExpenseReason } from "@/lib/expenseRules";
import { formatCLP } from "@/lib/format";
import MoneyInput from "@/components/MoneyInput";
import { computeEconomics } from "@/pages/wizard/economics";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

function AmountHint({ amount, onFix, idx }) {
  if (!amount) return <p className="text-xs text-muted-foreground mt-1">Escribe el monto completo en pesos. Ej: 45.000 para $45.000.</p>;
  if (amount > 0 && amount < 1000) {
    return (
      <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid={`expense-amount-warning-${idx}`}>
        Escribiste <strong>{formatCLP(amount)}</strong> al mes. ¿Quisiste decir <strong>{formatCLP(amount * 1000)}</strong>?{" "}
        <button type="button" onClick={onFix} className="underline font-semibold" data-testid={`expense-amount-fix-${idx}`}>Sí, corregir</button>
      </div>
    );
  }
  return <p className="text-xs text-muted-foreground mt-1" data-testid={`expense-amount-preview-${idx}`}>= {formatCLP(amount)} mensuales</p>;
}


const AREAS = [
  { key: "medicos", title: "Gastos Médicos Extraordinarios",
    desc: "Tratamientos, medicamentos permanentes, terapias y gastos de salud recurrentes no cubiertos." },
  { key: "necesidades_especiales", title: "Necesidades especiales / discapacidad", optional: true,
    desc: "Terapias, cuidadores, educación diferencial u otros gastos por discapacidad o necesidades especiales." },
  { key: "vivienda_adicional", title: "Vivienda adicional (familias separadas/divorciadas)",
    desc: "Mantención de una segunda vivienda producto de una separación o divorcio.",
    note: "Se descuenta el arriendo de la segunda vivienda (no la principal). Requiere contrato de arriendo, o el dividendo si es comprada. Máximo a descontar: 20% del ingreso, incluyendo cuentas y gastos comunes." },
  { key: "apoyo_familiares", title: "Apoyo a familiares en necesidad", related: true,
    desc: "Ayuda económica a padres u otros familiares dependientes." },
  { key: "otros", title: "Otros gastos extraordinarios recurrentes",
    desc: "Cualquier otro gasto extraordinario, recurrente e inevitable que no encaje en las áreas anteriores." },
];

// Mapea categorías antiguas (wizard previo) a las nuevas áreas, para reordenar solicitudes ya existentes.
const LEGACY_AREA = {
  "Gastos médicos recurrentes": "medicos",
  "Medicamentos permanentes": "medicos",
  "Tratamientos": "medicos",
  "Cuidado de familiares": "apoyo_familiares",
  "Apoyo económico a familiares dependientes": "apoyo_familiares",
  "Otra obligación familiar extraordinaria": "otros",
};
const AREA_KEYS = new Set(AREAS.map((a) => a.key));
const resolveArea = (e) => {
  if (e.area && AREA_KEYS.has(e.area)) return e.area;
  return LEGACY_AREA[e.category] || "otros";
};

const BlankItem = ({ area, onStart }) => (
  <div className="rounded-xl border border-dashed border-border bg-secondary/10 p-4" data-testid={`expense-blank-${area.key}`}>
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <Label className="text-xs font-semibold uppercase text-slate-500">Monto mensual en pesos (CLP)</Label>
        <MoneyInput placeholder="Ej: 45.000" className="h-12 rounded-xl mt-1.5" value={null} onChange={(v) => v && onStart({ amount: v })} data-testid={`expense-blank-amount-${area.key}`} />
      </div>
      {area.related && (
        <div>
          <Label className="text-xs font-semibold uppercase text-slate-500">Persona relacionada</Label>
          <Input className="h-12 rounded-xl mt-1.5" value="" onChange={(ev) => onStart({ related_person: ev.target.value })} data-testid={`expense-blank-related-${area.key}`} />
        </div>
      )}
      <div className={area.related ? "sm:col-span-2" : ""}>
        <Label className="text-xs font-semibold uppercase text-slate-500">Descripción</Label>
        <Textarea className="rounded-xl mt-1.5" value="" onChange={(ev) => onStart({ description: ev.target.value })} data-testid={`expense-blank-description-${area.key}`} />
      </div>
    </div>
    <p className="text-xs text-muted-foreground mt-3">Si no tienes gastos en esta área, déjalo en blanco. Al completar, podrás adjuntar el comprobante.</p>
  </div>
);

export default function StepExpenses({ app, patch, reload }) {
  const expenses = app.expenses || [];
  const eco = computeEconomics(app);

  // Los ítems sin "area" (solicitudes antiguas) se mapean por su categoría previa.
  const itemsFor = (areaKey) => expenses.map((e, idx) => ({ e, idx })).filter(({ e }) => resolveArea(e) === areaKey);

  const addItem = (area, init = {}) => patch({
    expenses: [...expenses, { id: uid(), area: area.key, category: area.title, description: "", amount: null, related_person: "", status: "pending", ...init }],
  });
  const hasDocs = (id) => (app.documents || []).some((d) => d.scope === "expense" && d.expense_id === id);
  const set = (idx, k, v) => {
    const e = [...expenses];
    const item = { ...e[idx], [k]: v };
    const empty = !item.amount && !(item.description || "").trim() && !(item.related_person || "").trim();
    if (empty && !hasDocs(item.id)) return patch({ expenses: e.filter((_, x) => x !== idx) });
    e[idx] = item;
    patch({ expenses: e });
  };
  const rm = (idx) => patch({ expenses: expenses.filter((_, x) => x !== idx) });
  const [focus, setFocus] = useState(null);
  const startItem = (area, init) => {
    const id = uid();
    addItem(area, { id, ...init });
    setFocus({ id, field: Object.keys(init)[0] });
  };
  const af = (e, field) => (focus && focus.id === e.id && focus.field === field
    ? { autoFocus: true, onFocus: (ev) => { const l = ev.target.value.length; ev.target.setSelectionRange?.(l, l); } } : {});

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-1">Gastos extraordinarios del grupo familiar</h1>
        <p className="text-muted-foreground">Considera únicamente gastos extraordinarios, recurrentes e inevitables que afecten significativamente la capacidad económica de tu familia. Agrega cada gasto en el área que corresponda.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex gap-3" data-testid="expenses-disclaimer">
        <Info className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          <strong>No</strong> se consideran gastos extraordinarios los costos normales de una familia, por ejemplo: dividendo o arriendo de la vivienda principal, alimentación, seguros (salud, vida, vehículos), educación/colegiatura regular, servicios básicos (luz, agua, gas, internet, teléfono), transporte y combustible, vehículos, vacaciones y viajes, y compras, inversiones o créditos de consumo por decisiones personales.
        </p>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex gap-3" data-testid="expenses-not-accepted-note">
        <Ban className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
        <p className="text-sm text-red-900">
          <strong>Gasto no aceptado como excepcional:</strong> arriendo o dividendo de la vivienda principal, gastos comunes, cuotas de autos, seguros, costo de colegio y alimentación del hogar. Simplemente no los incluyas; el sistema no permitirá enviarlos.
        </p>
      </div>

      {AREAS.map((area) => {
        const items = itemsFor(area.key);
        return (
          <section key={area.key} className="bg-white rounded-2xl border border-border p-6" data-testid={`expense-area-${area.key}`}>
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-primary">{area.title}</h2>
                {area.optional && <span className="text-[10px] uppercase font-semibold bg-secondary text-muted-foreground rounded-full px-2 py-0.5">Opcional</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{area.desc}</p>
              {area.note && (
                <div className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3" data-testid={`expense-area-note-${area.key}`}>
                  <Info className="w-4 h-4 text-blue-700 shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-900">{area.note}</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {items.map(({ e, idx }, n) => (
                <div key={e.id || idx} className="rounded-xl border border-border/70 bg-secondary/20 p-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-xs font-semibold text-muted-foreground">Ítem {n + 1}</span>
                    <button onClick={() => rm(idx)} className="text-destructive" data-testid={`btn-remove-expense-${idx}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label className="text-xs font-semibold uppercase text-slate-500">Monto mensual en pesos (CLP)</Label>
                      <MoneyInput placeholder="Ej: 45.000" className="h-12 rounded-xl mt-1.5" value={e.amount} onChange={(v) => set(idx, "amount", v)} {...af(e, "amount")} data-testid={`expense-amount-${idx}`} />
                      <AmountHint amount={e.amount} onFix={() => set(idx, "amount", e.amount * 1000)} idx={idx} />
                    </div>
                    {area.related && (
                      <div>
                        <Label className="text-xs font-semibold uppercase text-slate-500">Persona relacionada</Label>
                        <Input className="h-12 rounded-xl mt-1.5" value={e.related_person || ""} onChange={(ev) => set(idx, "related_person", ev.target.value)} {...af(e, "related_person")} data-testid={`expense-related-${idx}`} />
                      </div>
                    )}
                    <div className={area.related ? "sm:col-span-2" : ""}>
                      <Label className="text-xs font-semibold uppercase text-slate-500">Descripción</Label>
                      <Textarea className="rounded-xl mt-1.5" value={e.description || ""} onChange={(ev) => set(idx, "description", ev.target.value)} {...af(e, "description")} data-testid={`expense-description-${idx}`} />
                    </div>
                  </div>
                  {blockedExpenseReason(e) && (
                    <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900" data-testid={`expense-blocked-${idx}`}>
                      <Ban className="w-4 h-4 shrink-0 mt-0.5" />
                      <span><strong>Gasto no aceptado como excepcional</strong> ({blockedExpenseReason(e)}). No lo incluyas: elimina este ítem para poder enviar la solicitud.</span>
                    </div>
                  )}
                  <Label className="text-xs font-semibold uppercase text-slate-500">Comprobante (boletas, facturas, certificados, transferencias)</Label>
                  <div className="mt-2">
                    <DocumentUpload appId={app.id} scope="expense" expenseId={e.id} docType="expense" documents={app.documents} onChange={reload} />
                  </div>
                </div>
              ))}
              {items.length === 0 && <BlankItem area={area} onStart={(init) => startItem(area, init)} />}
            </div>

            {items.length > 0 && (
              <div className="mt-4 flex justify-between rounded-xl bg-secondary/40 px-4 py-3 text-sm" data-testid={`expense-subtotal-${area.key}`}>
                <span className="font-semibold text-slate-600">Subtotal {area.title}</span>
                <span className="font-bold text-primary">{formatCLP(items.reduce((s, { e }) => s + (Number(e.amount) || 0), 0))}</span>
              </div>
            )}

            <Button variant="outline" onClick={() => addItem(area)} className="rounded-xl border-2 mt-4" data-testid={`btn-add-expense-${area.key}`}>
              <Plus className="w-4 h-4 mr-1" /> {items.length ? "Agregar otro gasto" : "Agregar gasto"}
            </Button>
          </section>
        );
      })}

      <section className="bg-primary text-white rounded-2xl p-6">
        <h3 className="text-white text-base font-semibold mb-4">Resumen económico</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span>Ingreso familiar bruto mensual</span><span className="font-semibold">{formatCLP(eco.gross)}</span></div>
          <div className="flex justify-between"><span>Gastos extraordinarios</span><span className="font-semibold">− {formatCLP(eco.expenses)}</span></div>
          <div className="flex justify-between border-t border-white/20 pt-2 text-base"><span>Ingreso familiar ajustado</span><span className="font-bold text-accent">{formatCLP(eco.adjusted)}</span></div>
        </div>
        <p className="text-xs text-white/60 mt-3">Los gastos pueden quedar sujetos a validación administrativa.</p>
      </section>
    </div>
  );
}
